// Helpers purs de la section « Studio Story » (stories Instagram générées).
//
// Limites alignées sur le schéma du storyboard (studio/story-video, publié pour le Go
// dans comparator-aws/common/domain/model/studio) : l'API les applique aussi (422).

export const STUDIO_LIMITS = {
    hookTitle: 45,
    hookPunch: 45,
    label: 18,
    title: 45,
    subtitle: 90,
    badge: 18,
    badges: 3,
    outroLine: 45,
    outroLines: 3,
};

export const MODES = [
    { id: "general", label: "Généraliste (présente TRC)" },
    { id: "product", label: "Un seul produit" },
];

export const ANGLES = {
    general: [
        { id: "all", label: "Tout" },
        { id: "comparator", label: "Comparateur" },
        { id: "deals", label: "Bonnes affaires" },
        { id: "experience", label: "Expérience" },
        { id: "strava", label: "Strava" },
    ],
    product: [
        { id: "all", label: "Tout" },
        { id: "price", label: "Prix" },
        { id: "info", label: "Infos" },
        { id: "price-live", label: "Prix à jour" },
        { id: "strava", label: "Strava" },
    ],
};

// Généraliste : 3 emplacements libres (Comparateur → produit 1, Fiche → produit 2, Prix → produit 3).
// Produit : une fiche, plus la version de l'autre genre pour une pub mixte Homme + Femme.
export const SLOT_LABELS = {
    p1: "Produit 1 (chapitre Comparateur)",
    p2: "Produit 2 (chapitre Fiche produit)",
    p3: "Produit 3 (chapitre Prix à jour)",
    main: "Produit",
    main2: "Version de l'autre genre (pub mixte)",
};

// Catégories de recherche (headCategory de l'API produits).
export const CATEGORIES = [
    { id: "/products/shoes", label: "Chaussures" },
    { id: "/products/spikes", label: "Pointes" },
    { id: "/products/apparel", label: "Vêtements" },
    { id: "/products/electronic", label: "Électronique (montres, audio)" },
    { id: "/products/accessories", label: "Accessoires" },
    { id: "/products/nutrition", label: "Nutrition" },
    { id: "/products/triathlon", label: "Triathlon" },
];
export const defaultCategory = () => "/products/shoes";

export const slotsFor = (mode, mixed = false) => (mode === "general" ? ["p1", "p2", "p3"] : mixed ? ["main", "main2"] : ["main"]);

// Longueur en caractères (un « é » compte pour un).
export const charCount = (s) => [...String(s || "")].length;

function check(errors, label, value, max, optional = false) {
    const v = String(value || "").trim();
    if (!v) { if (!optional) errors.push(`${label} : vide`); }
    else if (charCount(v) > max) errors.push(`${label} : ${charCount(v)}/${max}`);
}

/** Erreurs de saisie (vide = textes valides). */
export function textErrors(texts) {
    const errors = [];
    if (!texts) return ["textes absents"];
    check(errors, "Accroche — titre", texts.hook?.title, STUDIO_LIMITS.hookTitle);
    check(errors, "Accroche — punch", texts.hook?.punch, STUDIO_LIMITS.hookPunch);
    (texts.chapters || []).forEach((c, i) => {
        const n = `Chapitre ${i + 1}`;
        check(errors, `${n} — label`, c.label, STUDIO_LIMITS.label);
        check(errors, `${n} — titre`, c.title, STUDIO_LIMITS.title);
        check(errors, `${n} — sous-titre`, c.subtitle, STUDIO_LIMITS.subtitle, true); // facultatif : non affiché
        const badges = c.badges || [];
        if (badges.length > STUDIO_LIMITS.badges) errors.push(`${n} — badges : ${badges.length}/${STUDIO_LIMITS.badges}`);
        badges.forEach((b) => check(errors, `${n} — badge`, b, STUDIO_LIMITS.badge));
    });
    const lines = texts.outro?.lines || [];
    if (!lines.length || lines.length > STUDIO_LIMITS.outroLines) errors.push(`Outro : ${lines.length}/${STUDIO_LIMITS.outroLines} lignes`);
    lines.forEach((l) => check(errors, "Outro — ligne", l, STUDIO_LIMITS.outroLine));
    return errors;
}

const STATUSES = {
    draft: { label: "Capture des pages…", color: "secondary", previewable: false, exportable: false, polling: true },
    capturing: { label: "Capture des pages…", color: "info", previewable: false, exportable: false, polling: true },
    captured: { label: "Prêt", color: "success", previewable: true, exportable: true, polling: false },
    rendering: { label: "Rendu en cours…", color: "warning", previewable: true, exportable: false, polling: true },
    done: { label: "Exporté", color: "primary", previewable: true, exportable: true, polling: false },
    failed: { label: "Échec", color: "danger", previewable: true, exportable: true, polling: false },
};

export const statusInfo = (status) => STATUSES[status] || { label: status || "—", color: "secondary", previewable: false, exportable: false, polling: false };

/** Raison d'empêcher l'enregistrement (null = possible). */
export function saveBlockedReason(status, texts) {
    if (status === "rendering") return "Un rendu en cours empêche de modifier la story : attends sa fin.";
    if (status === "draft" || status === "capturing") return "Capture des pages en cours : attends qu'elle se termine.";
    const errors = textErrors(texts);
    return errors.length ? `À corriger : ${errors.join(" ; ")}` : null;
}

// Les URL des captures sont présignées pour 1 h : on recharge la story avant expiration.
export const needsRefresh = (loadedAt, now) => now - loadedAt > 45 * 60 * 1000;

/** Message affichable pour une réponse d'erreur de l'API. */
export function apiErrorMessage(status, body) {
    if (status === 401) return "Session expirée : reconnecte-toi à pikachu.";
    const msg = body && typeof body.message === "string" ? body.message : "";
    if (msg) return msg;
    return `Erreur de l'API (HTTP ${status}).`;
}

// Chapitres de chaque angle : table identique à l'API (common/domain/model/studio_story.go).
const ANGLE_KINDS = {
    general: { all: ["curation", "comparator", "product", "price", "strava", "experience"], comparator: ["comparator", "price"], deals: ["curation", "price", "comparator"], experience: ["experience", "product"], strava: ["strava"] },
    product: { all: ["comparator", "product", "price", "strava"], price: ["comparator", "price"], info: ["product"], "price-live": ["price"], strava: ["strava"] },
};
// Le chapitre « strava » (bloc Strava des fiches chaussures) est retiré quand la story n'a aucune chaussure.
export const chapterKinds = (mode, angle, shoe = true) => [...((ANGLE_KINDS[mode] || {})[angle] || [])].filter((k) => shoe || k !== "strava");

/** La story contient-elle une chaussure ? (storyboard.products[].headCategory) */
export const hasShoe = (storyboard) => ((storyboard && storyboard.products) || []).some((p) => p.headCategory === "/products/shoes");

// Textes par défaut d'un chapitre (mêmes modèles que l'API en attendant la génération par Claude).
const CHAPTER_DEFAULTS = {
    curation: { label: "Curation", title: "Que du matos de runner", subtitle: "Chaussures, montres, audio : une sélection pensée pour les coureurs.", badges: ["Sélection"] },
    comparator: { label: "Comparateur", title: "Tous les marchands d'un coup", subtitle: "On compare les boutiques et on te montre le meilleur prix.", badges: [] },
    product: { label: "Fiche produit", title: "Tout savoir avant d'acheter", subtitle: "Specs, avis et guide des tailles au même endroit.", badges: [] },
    price: { label: "Prix à jour", title: "Le bon prix au bon moment", subtitle: "L'historique te dit si c'est le moment d'acheter.", badges: ["Alerte prix"] },
    experience: { label: "Expérience", title: "Pensé pour les coureurs", subtitle: "Favoris, alertes et conseils, sur mobile comme sur ordi.", badges: [] },
    strava: { label: "Strava", title: "Tes km valent des TRC Coins", subtitle: "Relie Strava : on suit l'usure de tes chaussures et chaque km te rapporte des TRC Coins.", badges: ["Strava", "TRC Coins"] },
};

/** Textes recomposés pour un nouvel angle : un chapitre conservé garde son texte, un nouveau prend le défaut. */
export function textsForAngle(mode, fromAngle, toAngle, texts, shoe = true) {
    const before = chapterKinds(mode, fromAngle, shoe);
    const byKind = {};
    before.forEach((k, i) => { if (texts.chapters && texts.chapters[i]) byKind[k] = texts.chapters[i]; });
    return {
        ...texts,
        chapters: chapterKinds(mode, toAngle, shoe).map((k) => ({ ...(byKind[k] || CHAPTER_DEFAULTS[k]), badges: [...((byKind[k] || CHAPTER_DEFAULTS[k]).badges || [])] })),
    };
}

/** Storyboard de l'aperçu : thème et textes en cours d'édition appliqués localement (retour instantané). */
export function previewStoryboard(storyboard, theme, texts, format = "story") {
    return {
        ...storyboard,
        theme,
        format,
        hook: texts.hook,
        outro: texts.outro,
        chapters: (storyboard.chapters || []).map((c, i) => ({ ...c, ...(texts.chapters && texts.chapters[i] ? texts.chapters[i] : {}) })),
    };
}

// Empreinte des captures sans la signature (les URL changent à chaque GET, les pages non).
const captureFingerprint = (caps) => {
    if (!caps) return "";
    const strip = (u) => String(u || "").split("?")[0];
    return JSON.stringify(Object.keys(caps).sort().map((k) => {
        const c = caps[k] || {};
        return [k, strip(c.page), strip(c.header), strip(c.bar), c.sizeFlow ? strip(c.sizeFlow.page) + strip(c.sizeFlow.panel) : ""];
    }));
};

/**
 * Story relue par le polling : statut, erreurs, rendus et vidéo mis à jour ; storyboard et captures
 * conservés (même objet) tant qu'ils n'ont pas changé, pour que le Player ne recharge pas les images
 * toutes les 3 s. `refresh` force les nouvelles URL (signatures bientôt expirées).
 */
export function mergePolledStory(prev, next, refresh) {
    if (!prev) return next;
    const sameBoard = JSON.stringify(prev.storyboard) === JSON.stringify(next.storyboard);
    const sameCaps = captureFingerprint(prev.captures) === captureFingerprint(next.captures);
    return {
        ...next,
        storyboard: sameBoard ? prev.storyboard : next.storyboard,
        captures: sameCaps && !refresh && prev.captures ? prev.captures : next.captures,
    };
}

/** Ce que montre la colonne aperçu : capture en cours, aperçu prêt, ou capture échouée (à recréer). */
export function previewState(status, hasCaptures) {
    if (hasCaptures) return "ready";
    return status === "failed" ? "capture-failed" : "capturing";
}

// Propositions du jour (GET /studio-deals) : emplacement de la deal pour chaque produit généraliste.
export const DEAL_SLOT_FOR = { p1: "shoes", p2: "watch", p3: "audio" };

// `day` = jour du calcul (la ligne de la veille est servie si celle du jour manque), `today` = YYYY-MM-DD UTC.
export const dealLabel = (deal, day, today = new Date().toISOString().slice(0, 10)) => {
    if (deal.fallback) return `-${deal.discountPct} % · remise du moment`;
    const when = !day || day === today ? "du jour" : `du ${day.slice(8, 10)}/${day.slice(5, 7)}`;
    return `-${deal.discountPct} % · baisse ${when}`;
};

// Langue de la story : site capturé, prix (jumeaux ES), textes et domaine de fin.
export const LOCALES = [
    { id: "fr", label: "Français", hint: "therunningcollective.fr" },
    { id: "es", label: "Espagnol", hint: "therunningcollective.es" },
];

export const localeLabel = (id) => (LOCALES.find((l) => l.id === id) || LOCALES[0]).label;

// Formats d'export : story 9:16 (stories, Reels) et publication 4:5 du fil Instagram (1080×1350).
export const FORMATS = [
    { id: "story", label: "Story 9:16", hint: "Stories et Reels (1080×1920)" },
    { id: "feed", label: "Publication 4:5", hint: "Fil Instagram (1080×1350)" },
];

export const formatLabel = (id) => (FORMATS.find((f) => f.id === id) || FORMATS[0]).label;
